<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotifications } from '@/composables/useNotifications'
import { useChatClaimsBadge } from '@/composables/useChatClaimsBadge'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

// Chat-claim pending count (the same one previously shown as a sidebar
// badge). Surfaced here in the bell so operators see "X verifications
// pending" alongside other notifications — visible without expanding
// the Customers menu. Polling is set up centrally by the layout via
// useChatClaimsBadgePoller(), so we only need to READ here.
const { count: chatClaimsCount } = useChatClaimsBadge()

// Dismiss-without-action: operator can hide the chat-claim entry until
// a NEW claim arrives. Persisted in sessionStorage so it survives
// navigation within the tab but resets on a full window/tab reload.
// The dismissed count is the snapshot at dismiss time; the entry re-
// appears once chatClaimsCount > dismissedAt.
const DISMISS_KEY = 'navbar.chatClaims.dismissedAt'
const chatClaimsDismissedAt = ref(parseInt(sessionStorage.getItem(DISMISS_KEY), 10) || -1)

const chatClaimsVisible = computed(() => {
  return chatClaimsCount.value > 0
    && (chatClaimsDismissedAt.value < 0 || chatClaimsCount.value > chatClaimsDismissedAt.value)
})

const dismissChatClaims = (e) => {
  if (e) { e.stopPropagation(); e.preventDefault() }
  chatClaimsDismissedAt.value = chatClaimsCount.value
  sessionStorage.setItem(DISMISS_KEY, String(chatClaimsCount.value))
}

const emit = defineEmits(['read', 'unread', 'remove', 'click:notification'])

const { t } = useI18n()

// ✅ Use notification composable
const {
  notifications,
  fetchNotifications,
  markRead,
  markAllRead,
  removeNotification: originalRemoveNotification,
} = useNotifications()

// ✅ Updated delete notification handler with improved API calling
async function deleteNotification(id, event) {
  try {
    
    // Prevent click from triggering mark as read if event exists
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }
    
    // Check if this is a server-side notification
    // UUID pattern for Laravel notifications
    const uuidPattern = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i
    const isServerNotification = uuidPattern.test(String(id))
    
    // Numeric IDs could also be server notifications
    const isNumericId = !isNaN(parseInt(id)) && String(id).length < 20
    
    const shouldCallAPI = isServerNotification || isNumericId
    
    if (shouldCallAPI) {
      // Get token from localStorage or cookie
      const token = localStorage.getItem('accessToken') || document.cookie.split('; ')
        .find(row => row.startsWith('accessToken='))
        ?.split('=')[1]
      
      if (!token) {
        console.error('🛑 No auth token found, cannot delete notification')
        
        return
      }
      
      
      try {
        // Make direct fetch request for best cross-browser compatibility
        const url = `/api/notifications/${id}`
        
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        
        const responseData = await response.json()
        
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}: ${responseData.message || 'Unknown error'}`)
        }
        
      } catch (fetchError) {
        console.error('🛑 Fetch Error:', fetchError)
        
        // If fetch fails, try axios as backup
        try {
          
          // Try /api/notifications/ path first
          const axiosResponse = await axios.delete(`/api/notifications/${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          })
          
        } catch (axiosError) {
          console.error('🛑 Both fetch and axios failed:', axiosError)
          
          // Try one more time with the non-prefixed path as last resort
          try {
            const lastResponse = await axios.delete(`/notifications/${id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            })
            
          } catch (finalError) {
            console.error('🛑 All attempts failed - notification may still be in database')
          }
        }
      }
    } else {
    }
    
    // Remove from local state regardless
    originalRemoveNotification(id)
    
    // Emit remove event for parent components
    emit('remove', id)
  } catch (error) {
    console.error('Error in deleteNotification:', error)
  }
}

// Override the original removeNotification with our enhanced version
const removeNotification = deleteNotification

// Register notification functions globally to allow direct access from other components
function registerGlobalNotificationFunctions() {
  // Create global registry for notification functions
  window.notificationFunctions = {
    addDirectNotification: notification => {
      // Use the composable's addNotification function
      const { addNotification } = useNotifications()

      addNotification(notification)
      
      return true
    },
    refreshNotifications: async () => {
      await fetchNotifications()
      
      return true
    },
  }
}

// Listen for fallback notification events
function setupFallbackListeners() {
  // Listen for fallback notification events from WebSocket
  window.addEventListener('parcel:created:fallback', event => {
    
    if (event.detail && event.detail.parcel) {
      // Note: Fallback notifications are skipped to avoid invalid ID warnings
      // Server-side notifications with proper IDs should handle persistent notifications
      console.log('Fallback parcel notification received but skipped to prevent invalid ID warnings:', event.detail.parcel.barcode)
    }
  })
  
  // Also listen for the notification event
  window.addEventListener('notification', event => {
    const { addNotification } = useNotifications()

    addNotification(event.detail)
  })
}

// Setup polling for notifications
//
// Disabled 2026-05-08 alongside the kill switch in useNotifications. The
// 30s timer was firing GET /api/notifications every half-minute against
// an unused feature. Re-enable in tandem with NOTIFICATIONS_REMOTE_ENABLED
// in useNotifications.js when notifications come back online.
function setupNotificationPolling() {
  // No-op while remote notifications are off.
  return () => {}
}

// ✅ Setup on component mount
onMounted(() => {
  // Load initial notifications
  fetchNotifications()
  
  // Register global notification functions
  registerGlobalNotificationFunctions()
  
  // Setup fallback listeners
  setupFallbackListeners()
  
  // Setup polling
  const cleanupPolling = setupNotificationPolling()
  
  // Store cleanup function for onUnmounted
  onUnmounted(cleanupPolling)
})

// Format date
const formatDate = dateString => {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMinutes = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffHours < 24) {
      if (diffMinutes < 1) return t('common.just_now')
      if (diffMinutes < 60) return t('common.minutes_ago', { count: diffMinutes })
      
      return t('common.hours_ago', { count: diffHours })
    }

    // If less than 7 days ago, show days ago
    if (diffDays < 7) {
      return t('common.days_ago', { count: diffDays })
    }

    // Otherwise show formatted date according to locale
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(date)
  } catch (error) {
    return dateString
  }
}

const router = useRouter()

// ✅ Computed properties
// Sum of unread parcel notifications + visible chat-claims.
// chat-claims contribute only when not dismissed (or fresh ones arrived
// since dismissal).
const totalUnseenNotifications = computed(() => {
  const claims = chatClaimsVisible.value ? chatClaimsCount.value : 0
  return notifications.value.filter(n => !n.read_at).length + claims
})

// Quick navigation to the chat-identity claim approval queue.
const openChatClaimsQueue = () => {
  router.push({ name: 'shipment-customers-identification-queue' })
}

const isAllMarkRead = computed(() => {
  return notifications.value.some(n => !n.read_at)
})

// ✅ Mark all notifications as read or unread
const markAllReadOrUnread = async () => {
  if (!isAllMarkRead.value) {
    emit('unread', notifications.value.map(n => n.id))
  } else {
    await markAllRead()
    emit('read', notifications.value.map(n => n.id))
  }
}

// ✅ Toggle read/unread state of a notification
const toggleReadUnread = async (isSeen, id) => {
  try {
    if (isSeen) {
      emit('unread', [id])
    } else {
      await markRead(id)
      emit('read', [id])
    }
  } catch (error) {
    console.warn('WARNING: Failed to toggle notification read state:', error)
    // Don't break the UI
  }
}

// ✅ Handle notification click
const handleNotificationClick = async notification => {
  try {
    
    if (!notification.read_at) {
      await markRead(notification.id)
    }
    
    // Handle parcel-related notifications by emitting an event with the notification data
    if (notification.data?.type?.includes('parcel_') && notification.data?.parcel_id) {

      window.dispatchEvent(new CustomEvent('notification:click', {
        detail: {
          type: 'parcel',
          parcelId: notification.data.parcel_id,
        },
      }))
      
      // If we're on a page that can't handle the notification, navigate to the appropriate page
      if (!router.currentRoute.value.path.includes('/shipment')) {
        router.push(`/shipment/incoming-parcels`)
      }
    } else {
      // For other types of notifications
      emit('click:notification', notification)
    }
  } catch (error) {
    console.error('🔔 [Error] Failed to handle notification click:', error)
    // Don't break the UI - still allow the click to proceed
  }
}

// ✅ View all notifications
const viewAllNotifications = () => {
  router.push('/notifications')
}
</script>

<template>
  <IconBtn id="notification-btn">
    <VBadge
      :model-value="totalUnseenNotifications > 0"
      color="error"
      offset-x="2"
      offset-y="3"
      class="notification-badge"
    >
      <VIcon icon="tabler-bell" />
    </VBadge>

    <VMenu
      activator="parent"
      width="380px"
      offset="12px"
      :close-on-content-click="false"
    >
      <VCard class="d-flex flex-column">
        <!-- Header -->
        <VCardItem class="notification-section">
          <VCardTitle class="text-h6">
            {{ t('notifications.notifications') }}
          </VCardTitle>

          <template #append>
            <VChip
              v-show="totalUnseenNotifications > 0"
              size="small"
              color="primary"
              class="me-2"
            >
              {{ totalUnseenNotifications }} {{ t('notifications.new') }}
            </VChip>
            <IconBtn
              v-show="notifications.length"
              size="34"
              @click="markAllReadOrUnread"
            >
              <VIcon
                size="20"
                color="high-emphasis"
                :icon="isAllMarkRead ? 'tabler-mail' : 'tabler-mail-opened'"
              />
              <VTooltip
                activator="parent"
                location="start"
              >
                {{ isAllMarkRead ? t('notifications.mark_all_as_read') : t('notifications.mark_all_as_unread') }}
              </VTooltip>
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <!-- Notification list -->
        <PerfectScrollbar
          :options="{ wheelPropagation: false }"
          style="max-height: 23.75rem;"
        >
          <!-- Pinned chat-identity claims (always at top while visible).
               Routes to the queue page on click. Dismiss button hides the
               entry until a new claim arrives (sessionStorage-backed). -->
          <VList
            v-if="chatClaimsVisible"
            class="rounded-0 py-0"
          >
            <VListItem
              link
              min-height="66px"
              class="list-item-hover-class bg-warning-tonal"
              @click="openChatClaimsQueue"
            >
              <div class="d-flex align-start gap-3">
                <VAvatar color="warning" variant="tonal">
                  <VIcon icon="tabler-id-badge-2" size="20" />
                </VAvatar>
                <div class="flex-grow-1 min-w-0">
                  <p class="text-sm font-weight-medium mb-1">
                    {{ t('notifications.chat_claims_pending_title') }}
                  </p>
                  <p class="text-body-2 mb-0" style="letter-spacing: 0.4px;">
                    {{ t('notifications.chat_claims_pending_message', { count: chatClaimsCount }) }}
                  </p>
                </div>
                <div class="d-flex flex-column align-end gap-1">
                  <VChip size="small" color="warning" variant="flat">
                    {{ chatClaimsCount }}
                  </VChip>
                  <VBtn
                    size="x-small"
                    variant="text"
                    color="medium-emphasis"
                    icon="tabler-x"
                    :title="t('notifications.dismiss')"
                    @click.stop.prevent="dismissChatClaims"
                  />
                </div>
              </div>
            </VListItem>
            <VDivider />
          </VList>

          <VList
            v-if="notifications.length"
            class="notification-list rounded-0 py-0"
          >
            <template
              v-for="(notification, index) in notifications"
              :key="notification.id"
            >
              <VDivider v-if="index > 0" />
              <VListItem
                link
                min-height="66px"
                class="list-item-hover-class"
                :class="{ 'notification-read': notification.read_at }"
                @click="handleNotificationClick(notification)"
              >
                <div class="d-flex align-start gap-3">
                  <VAvatar
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-bell"
                      size="20"
                    />
                  </VAvatar>

                  <div>
                    <p class="text-sm font-weight-medium mb-1">
                      {{ notification.data?.title_key ? t(notification.data.title_key) : notification.data?.title || t('notifications.new_notification') }}
                    </p>
                    <p
                      class="text-body-2 mb-2"
                      style="letter-spacing: 0.4px;"
                    >
                      {{ notification.data?.message_key ?
                        t(notification.data.message_key, notification.data.message_data || {}) :
                        notification.data?.message || t('notifications.no_content') }}
                    </p>
                    <p class="text-sm text-disabled mb-0">
                      {{ formatDate(notification.created_at) }}
                    </p>
                  </div>
                  <VSpacer />

                  <div class="d-flex flex-column align-end">
                    <VIcon
                      size="10"
                      icon="tabler-circle-filled"
                      :color="!notification.read_at ? 'primary' : '#a8aaae'"
                      class="mb-2"
                      @click.stop="toggleReadUnread(notification.read_at, notification.id)"
                    />

                    <VBtn
                      size="x-small"
                      icon
                      variant="text"
                      color="error"
                      class="visible-in-hover delete-notification-btn"
                      title="Delete notification"
                      @click.stop.prevent="removeNotification(notification.id, $event)"
                    >
                      <VIcon
                        size="20"
                        icon="tabler-trash"
                      />
                    </VBtn>
                  </div>
                </div>
              </VListItem>
            </template>
          </VList>

          <VListItem
            v-show="notifications.length === 0 && !chatClaimsVisible"
            class="text-center text-medium-emphasis"
          >
            <VListItemTitle>{{ t('notifications.no_notifications_found') }}</VListItemTitle>
          </VListItem>
        </PerfectScrollbar>

        <VDivider />

        <!-- Footer -->
        <VCardText
          v-show="notifications.length"
          class="pa-4"
        >
          <VBtn
            block
            size="small"
            color="primary"
            @click="viewAllNotifications"
          >
            {{ t('notifications.view_all_notifications') }}
          </VBtn>
        </VCardText>
      </VCard>
    </VMenu>
  </IconBtn>
</template>

<style lang="scss">
.notification-section {
  padding-block: 0.75rem;
  padding-inline: 1rem;
}

.list-item-hover-class {
  .visible-in-hover {
    display: none;
  }

  &:hover {
    .visible-in-hover {
      display: block;
    }
  }
}

.notification-list.v-list {
  .v-list-item {
    padding-block: 0.75rem !important;
    position: relative;

    &.notification-read {
      opacity: 0.8;
    }
    
    .delete-notification-btn {
      transition: all 0.2s ease;
      
      &:hover {
        background-color: rgb(var(--v-theme-error), 0.1);
        transform: scale(1.1);
      }
    }
  }
}

.notification-badge {
  .v-badge__badge {
    min-width: 18px;
    padding: 0;
    height: 18px;
  }
}
</style>
